import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { RMSPlanReviewScheduleService } from '../../../../shared/services/rmsPlanReviewSchedule.service';
import { Icons } from '../../../../shared/helpers/icons';
import { IRmsPlanReviewSchedule } from '../../../../shared/models/rmsPlanReviewSchedule';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { RmsScheduleOfficialSignatureComponent } from './rms-schedule-official-signature/rms-schedule-official-signature.component';
import { RmsScheduleSubmissionComponent } from './rms-schedule-submission/rms-schedule-submission.component';
import { RmsScheduleReviewComponent } from './rms-schedule-review/rms-schedule-review.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'rms-plan-review-schedule',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    PopupMessageComponent,
    SpinnerComponent,
    RmsScheduleReviewComponent,
    RmsScheduleOfficialSignatureComponent,
    RmsScheduleSubmissionComponent
  ],
  templateUrl: './rms-plan-review-schedule.component.html',
  styleUrl: './rms-plan-review-schedule.component.css'
})
export class RmsPlanReviewScheduleComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentRmsPlanReviewSchedule: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'schedule_review_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'official_signatures_section_completion',
      nextStepDependency: 'schedule_review_section_completion'
    },
  ];
  finalStepConfig = {
      id: 3,
      completionProperty: 'rms_plan_review_schedule_completion',
      nextStepDependency: 'official_signatures_section_completion'
  };
  steps = [1, 2];
  currentStep: number = 1;
  tooltips = ['Schedule Review', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private rmsPlanReviewScheduleService: RMSPlanReviewScheduleService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadRmsPlanReviewSchedule();
  }

  loadRmsPlanReviewSchedule(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.rmsPlanReviewScheduleService.getRmsPlanReviewScheduleByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentRmsPlanReviewSchedule = null;
            this.loading = false;
            return
          } else {
            this.currentRmsPlanReviewSchedule = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.popupMessage = error.message;
          this.messageModal = 'unsuccess';
        }
      });
    }
  }

  handleUpdateRoarAssignmentForm(data: IRmsPlanReviewSchedule, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.rmsPlanReviewScheduleService.updateRmsPlanReviewSchedule(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadRmsPlanReviewSchedule();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 3 ) {
            this.router.navigate(['/plans/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating rms plan review schedule:', error);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/plans/']);
  }
  
  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentRmsPlanReviewSchedule) {
      return this.currentRmsPlanReviewSchedule?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
